import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTabAuth } from '@/hooks/useTabAuth'
import { useAuth } from '../../hooks/useAuth'
import MainHeader from './_components/MainHeader'
import MainSidebar from './_components/MainSidebar'
import ChatModule, { ChatModuleRef } from './chat'
import ContactsModule from './contacts'
import SettingsModule from './settings'
import MessageManager from '@/utils/ably-im/message-manager'
import { useFriendStore } from '@/stores/useFriendStore'
import { useGroupStore } from '@/stores/useGroupStore'
import { useChatStore } from '@/stores/useChatStore'
import AblyManager, { GroupManagementNotificationData, GroupMessageNotificationData } from '@/utils/ably-im/ably-manager'
import { MessageInfo } from '@/api/types/chat'
import ToastUtils from '@/utils/toast-utils'
import { useChatActions } from '@/hooks/useChatActions'
import { useMessageHandler } from '@/hooks/useMessageHandler'
import { useNotificationSystem } from '@/hooks/useNotificationSystem'
import { useGroupNotificationHandler } from '@/hooks/useGroupNotificationHandler'
import { MainPageType, usePageDataStore } from '@/stores/usePageDataStore'
import { logPrint } from '@/utils/my-utils'

// 简单的加载组件
function LoadingSpinner({ message }: { message: string }) {
  return (
    <div className="h-screen bg-gray-100 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">{message}</p>
      </div>
    </div>
  )
}



export default function MainPage(): React.JSX.Element {
  const navigate = useNavigate()

  const mainPageMenu = usePageDataStore(state => state.mainPageType)
  const setMainPageMenu = usePageDataStore(state => state.setMainPageMenu)

  const tabAuth = useTabAuth()
  const { refreshUserInfo } = useAuth()
  const [isRefreshing, setIsRefreshing] = useState(false)

  // 检查登录状态（基于真实的认证token）
  const isAuthenticated = tabAuth.isAuthenticated()

  // 使用 useRef 来防止重复初始化
  const initializeOnce = useRef(false)

  // ChatModule 的引用，用于调用其方法
  const chatModuleRef = useRef<ChatModuleRef>(null)

  // 使用消息处理 Hook
  const messageHandler = useMessageHandler()

  // 使用通知系统 Hook
  const notificationSystem = useNotificationSystem()

  // 使用群组通知处理器 Hook
  const groupNotificationHandler = useGroupNotificationHandler()

  // 保存通知点击处理器的引用
  const notificationClickHandlerRef = useRef<((event: any) => void) | null>(null)

  logPrint("主页面====")

  useEffect(() => {
    // 只有在数据完全就绪后才进行认证检查
    if (tabAuth.isReady) {
      if (!isAuthenticated) {
        console.log('未认证，跳转到首页')
        navigate('/auth/organization')
        initializeOnce.current = false // 重置初始化标志
      } else if (!initializeOnce.current) {
        console.log('已认证，继续留在主页面')
        init();
        initializeOnce.current = true // 标记已初始化
      }
    } else {
      console.log('认证数据加载中，等待就绪...')
    }
  }, [isAuthenticated, navigate, tabAuth.isReady])

  // 初始化通知系统
  useEffect(() => {
    if (isAuthenticated && tabAuth.isReady) {
      initializeNotification();
    }
  }, [isAuthenticated, tabAuth.isReady])

  // 组件卸载时清理资源
  useEffect(() => {
    return () => {
      // 清理所有监听器
      MessageManager.getInstance().disconnect()
      AblyManager.getInstance().removeConnectionListener(onConnectionListener);
      AblyManager.getInstance().removeMessageListener(onMessageListener);
      AblyManager.getInstance().removeGroupManagementListener(onGroupManagementListener);
      AblyManager.getInstance().removeGroupMessageListener(onGroupMessageListener);

      // 清理通知系统
      notificationSystem.cleanupNotification()

      // 移除通知点击监听器
      if (notificationClickHandlerRef.current) {
        window.removeEventListener('notification-click', notificationClickHandlerRef.current)
      }
    }
  }, [])

  const setIsConnected = useChatStore((state: any) => state.setIsConnected);
  const { getConversationsAndLastMessage } = useChatActions();

  // 初始化通知系统
  const initializeNotification = async () => {
    try {
      // 初始化通知系统
      await notificationSystem.initializeNotification()

      // 创建并监听通知点击事件
      const notificationClickHandler = messageHandler.createNotificationClickHandler(
        navigateToUserChat,
        navigateToGroupChat
      )
      window.addEventListener('notification-click', notificationClickHandler)

      // 保存处理器引用以便清理
      notificationClickHandlerRef.current = notificationClickHandler
    } catch (error) {
      console.error('初始化通知系统失败:', error)
    }
  }


  const init = async () => {
    try {
      ToastUtils.showLoading(true, "初始化中...");

      await useGroupStore.getState().getGroupList();
      await useFriendStore.getState().getFriendList();
      useFriendStore.getState().getBlockedList();

      // 监听连接状态
      await resetConnection();

      await getConversationsAndLastMessage();

      console.log('应用初始化完成')
    } catch (error) {
      console.error('应用初始化失败:', error)
    } finally {
      ToastUtils.showLoading(false);
    }
  }

  // 重置监听状态
  const resetConnection = async () => {
    await AblyManager.getInstance().disconnect();
    AblyManager.getInstance().removeConnectionListener(onConnectionListener);
    AblyManager.getInstance().removeMessageListener(onMessageListener);
    AblyManager.getInstance().removeGroupManagementListener(onGroupManagementListener);
    AblyManager.getInstance().removeGroupMessageListener(onGroupMessageListener);

    // 初始化 Ably 连接
    await AblyManager.getInstance().initialize();

    // 订阅自己 和 自己加入的所有群组
    await MessageManager.getInstance().subscribeToExistingGroups();

    // 监听连接状态
    AblyManager.getInstance().addConnectionListener(onConnectionListener);

    // 监听消息
    AblyManager.getInstance().addMessageListener(onMessageListener);

    // 监听群组通知
    AblyManager.getInstance().addGroupManagementListener(onGroupManagementListener);
    AblyManager.getInstance().addGroupMessageListener(onGroupMessageListener);
  }

  // 监听连接状态
  const onConnectionListener = (isConnected: boolean) => {
    setIsConnected(isConnected)
  }

  // 监听消息（使用封装的消息处理器）
  const onMessageListener = async (message: MessageInfo) => {
    await messageHandler.handleIncomingMessage(message)
  }

  // 监听群组通知
  const onGroupManagementListener = async (data: GroupManagementNotificationData) => {
    await groupNotificationHandler.handleGroupManagementNotification(data)
  }

  // 监听群组消息通知
  const onGroupMessageListener = async (data: GroupMessageNotificationData) => {
    await groupNotificationHandler.handleGroupMessageNotification(data)
  }

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      // 刷新用户信息
      const success = await refreshUserInfo()
      if (success) {
        console.log('用户信息刷新成功')
        // 刷新整个页面
        window.location.reload()
      } else {
        console.error('用户信息刷新失败')
      }
    } catch (error) {
      console.error('刷新过程中发生错误:', error)
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleModuleChange = (module: MainPageType) => {
    setMainPageMenu(module)
  }

  // 导航到指定用户的聊天
  const navigateToUserChat = async (userId: string) => {
    // 首先切换到聊天模块
    setMainPageMenu(MainPageType.CHAT)

    // 等待下一个渲染周期，确保 ChatModule 已经渲染
    setTimeout(async () => {
      if (chatModuleRef.current) {
        await chatModuleRef.current.navigateToUserChat(userId)
      }
    }, 100)
  }

  // 导航到指定群组的聊天
  const navigateToGroupChat = async (conversationId: string) => {
    // 首先切换到聊天模块
    setMainPageMenu(MainPageType.CHAT)

    // 等待下一个渲染周期，确保 ChatModule 已经渲染
    setTimeout(async () => {
      if (chatModuleRef.current) {
        await chatModuleRef.current.navigateToGroupChat(conversationId)
      }
    }, 100)
  }

  if (!tabAuth.isReady) {
    return <LoadingSpinner message="初始化中..." />
  }

  const renderActiveModule = () => {
    switch (mainPageMenu) {
      case MainPageType.CHAT:
        return <ChatModule ref={chatModuleRef} />
      case MainPageType.CONTACTS:
        return <ContactsModule onNavigateToUserChat={navigateToUserChat} onNavigateToGroupChat={navigateToGroupChat} />
      case MainPageType.SETTINGS:
        return <SettingsModule />
    }
  }

  return (
    <div className="h-screen bg-gray-100 flex flex-col">
      <MainHeader
        currentUser={tabAuth.getUserInfo() || undefined}
        organizationCode={tabAuth.getOrganizationCode() || undefined}
        isRefreshing={isRefreshing}
        onRefresh={handleRefresh}
      />

      <div className="flex-1 flex overflow-hidden">
        <MainSidebar
          mainPageMenu={mainPageMenu}
          onModuleChange={handleModuleChange}
          onResetConnection={resetConnection}
        />

        <div className="flex-1 overflow-hidden">
          {renderActiveModule()}
        </div>
      </div>
    </div>
  )
}
